import { useCallback, useMemo } from "react";
import { queries } from "../../shared/queries";
import { isTenantScopeGrant } from "../../shared/rbac/permissions";
import { BUILTIN_ROLES, type BuiltinRoleId } from "../../shared/rbac/roles";
import { customRolesForScope } from "../lib/grantAssembly";
import { useQuery } from "./useQuery";

export type TenantRole = {
  id: string;
  label: string;
  kind: "builtin" | "custom";
};

export const ASSIGNABLE_TENANT_BUILTIN_ROLES: {
  id: BuiltinRoleId;
  label: string;
}[] = (
  Object.entries(BUILTIN_ROLES) as [
    BuiltinRoleId,
    (typeof BUILTIN_ROLES)[BuiltinRoleId],
  ][]
)
  .filter(([, r]) => r.scopeKind === "tenant" && r.userFacing)
  .map(([id, r]) => ({ id, label: r.label }));

export function useTenantRoles({
  grants,
  onCommit,
}: {
  grants: readonly {
    scopeId: string;
    tenantId?: string | null;
    builtinRoleId: string | null;
    customRoleId: string | null;
  }[];
  onCommit: (builtinRoleIds: string[], customRoleIds: string[]) => void;
}) {
  const [customRoles] = useQuery(queries.allRolesLite());

  const tenantCustomRoles = useMemo(
    () => customRolesForScope(customRoles, "tenant"),
    [customRoles]
  );

  const allTenantRoles: TenantRole[] = useMemo(
    () => [
      ...ASSIGNABLE_TENANT_BUILTIN_ROLES.map((r) => ({
        id: r.id,
        label: r.label,
        kind: "builtin" as const,
      })),
      ...tenantCustomRoles.map((r) => ({
        id: r.id,
        label: r.name,
        kind: "custom" as const,
      })),
    ],
    [tenantCustomRoles]
  );

  const assignedBuiltinRoleIds = useMemo(() => {
    const set = new Set<string>();
    for (const g of grants) {
      if (isTenantScopeGrant(g) && g.builtinRoleId) {
        set.add(g.builtinRoleId);
      }
    }
    return set;
  }, [grants]);

  const assignedCustomRoleIds = useMemo(() => {
    const set = new Set<string>();
    for (const g of grants) {
      if (isTenantScopeGrant(g) && g.customRoleId) {
        set.add(g.customRoleId);
      }
    }
    return set;
  }, [grants]);

  const toggleTenantRole = useCallback(
    (role: TenantRole) => {
      const nextBuiltin = new Set(assignedBuiltinRoleIds);
      const nextCustom = new Set(assignedCustomRoleIds);
      if (role.kind === "builtin") {
        if (nextBuiltin.has(role.id)) nextBuiltin.delete(role.id);
        else nextBuiltin.add(role.id);
      } else {
        if (nextCustom.has(role.id)) nextCustom.delete(role.id);
        else nextCustom.add(role.id);
      }
      onCommit([...nextBuiltin], [...nextCustom]);
    },
    [onCommit, assignedBuiltinRoleIds, assignedCustomRoleIds]
  );

  return {
    allTenantRoles,
    assignedBuiltinRoleIds,
    assignedCustomRoleIds,
    toggleTenantRole,
  };
}
